import React, { useState, useEffect, useCallback } from "react";
import Table from "@material-ui/core/Table";
import TableHead from "@material-ui/core/TableHead";
import TableBody from "@material-ui/core/TableBody";
import TableRow from "@material-ui/core/TableRow";
import TableCell from "@material-ui/core/TableCell";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import Chip from "@material-ui/core/Chip";

import GridItem from "components/Grid/GridItem.js";
import GridContainer from "components/Grid/GridContainer.js";
import Card from "components/Card/Card.js";
import CardHeader from "components/Card/CardHeader.js";
import CardBody from "components/Card/CardBody.js";
import Button from "components/CustomButtons/Button.js";

import { API_URI } from "../../helpers";

// Colores por estado (chips + badges de fila).
const STATUS_COLOR = {
  pending: "#94A3B8",
  sending: "#3B82F6",
  sent: "#16A34A",
  failed: "#DC2626",
  canceled: "#6B7280",
};

function StatusBadge({ status }) {
  return (
    <span style={{
      background: STATUS_COLOR[status] || "#94A3B8",
      color: "#fff", borderRadius: 6, padding: "2px 8px",
      fontSize: 12, fontWeight: 600, textTransform: "capitalize",
    }}>{status}</span>
  );
}

const CHANNELS = ["all", "email", "whatsapp"];
const STATUSES = ["all", "pending", "sending", "sent", "failed", "canceled"];

export default function Outbox() {
  const [rows, setRows] = useState([]);
  const [pagination, setPagination] = useState({ current_page: 1, last_page: 1, total: 0 });
  const [stats, setStats] = useState(null);
  const [channel, setChannel] = useState("all");
  const [status, setStatus] = useState("all");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(null);

  const fetchList = useCallback((page = 1) => {
    setLoading(true);
    const qs = new URLSearchParams({ channel, status, search, page });
    fetch(`${API_URI}/admin/outbox?${qs.toString()}`)
      .then((r) => r.json())
      .then((res) => {
        if (!res || !res.result) return;
        setRows(res.data || []);
        setPagination(res.pagination || { current_page: 1, last_page: 1, total: 0 });
        setStats(res.stats || null);
      })
      .finally(() => setLoading(false));
  }, [channel, status, search]);

  useEffect(() => { fetchList(1); }, [channel, status]); // eslint-disable-line react-hooks/exhaustive-deps

  function openPreview(id) {
    fetch(`${API_URI}/admin/outbox/${id}`)
      .then((r) => r.json())
      .then((res) => { if (res && res.result) setPreview(res.data); });
  }

  function retry(id) {
    fetch(`${API_URI}/admin/outbox/retry`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ id }),
    }).then((r) => r.json()).then(() => fetchList(pagination.current_page));
  }

  function retryAllFailed() {
    if (!window.confirm("¿Reencolar TODOS los fallidos?")) return;
    fetch(`${API_URI}/admin/outbox/retry`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ all_failed: true }),
    }).then((r) => r.json()).then(() => fetchList(pagination.current_page));
  }

  function cancelRow(id) {
    if (!window.confirm("¿Cancelar este envío?")) return;
    fetch(`${API_URI}/admin/outbox/cancel`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ id }),
    }).then((r) => r.json()).then(() => fetchList(pagination.current_page));
  }

  const fmt = (d) => (d ? String(d).replace("T", " ").substring(0, 16) : "—");

  return (
    <GridContainer>
      {/* Chips de estado + kill-switch */}
      <GridItem xs={12}>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", marginBottom: 8 }}>
          {stats && (
            <>
              <Chip label={`Pendientes ${stats.pending}`} style={{ background: STATUS_COLOR.pending, color: "#fff" }} />
              <Chip label={`Enviando ${stats.sending}`} style={{ background: STATUS_COLOR.sending, color: "#fff" }} />
              <Chip label={`Enviados ${stats.sent}`} style={{ background: STATUS_COLOR.sent, color: "#fff" }} />
              <Chip label={`Fallidos ${stats.failed}`} style={{ background: STATUS_COLOR.failed, color: "#fff" }} />
              <Chip label={`Listos ya ${stats.due_now}`} variant="outlined" />
              <Chip label={`24h: ${stats.sent_24h}`} variant="outlined" />
              <Chip
                label={stats.enabled ? "Motor: ON" : "Motor: OFF (no envía)"}
                style={{ background: stats.enabled ? STATUS_COLOR.sent : STATUS_COLOR.failed, color: "#fff", fontWeight: 700 }}
              />
            </>
          )}
        </div>
      </GridItem>

      <GridItem xs={12}>
        <Card>
          <CardHeader color="info">
            <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
              <span style={{ color: "#fff", fontWeight: 500 }}>Envíos (Outbox)</span>
              <span style={{ flex: 1 }} />
              <label style={{ color: "#fff", fontSize: 13 }}>Canal:&nbsp;
                <select value={channel} onChange={(e) => setChannel(e.target.value)}>
                  {CHANNELS.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </label>
              <label style={{ color: "#fff", fontSize: 13 }}>Estado:&nbsp;
                <select value={status} onChange={(e) => setStatus(e.target.value)}>
                  {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </label>
              <input
                placeholder="buscar destinatario / asunto"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") fetchList(1); }}
                style={{ padding: "4px 8px", borderRadius: 4, border: "none" }}
              />
              <Button color="white" size="sm" onClick={() => fetchList(1)}>Buscar</Button>
              <Button color="danger" size="sm" onClick={retryAllFailed}>Reintentar fallidos</Button>
            </div>
          </CardHeader>
          <CardBody>
            <Table>
              <TableHead>
                <TableRow>
                  {["ID", "Canal", "Destinatario", "Asunto / Template", "Estado", "Int.", "Programado", "Enviado", "Acciones"].map((h) => (
                    <TableCell key={h} style={{ fontWeight: 600 }}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.length === 0 && (
                  <TableRow><TableCell colSpan={9} style={{ textAlign: "center", color: "#94A3B8" }}>
                    {loading ? "Cargando…" : "Sin envíos para el filtro."}
                  </TableCell></TableRow>
                )}
                {rows.map((r) => (
                  <TableRow key={r.id}>
                    <TableCell>{r.id}</TableCell>
                    <TableCell>{r.channel}</TableCell>
                    <TableCell>{r.recipient}</TableCell>
                    <TableCell>
                      <div>{r.subject || <em style={{ color: "#94A3B8" }}>(sin asunto)</em>}</div>
                      <div style={{ fontSize: 11, color: "#94A3B8" }}>{r.template_key}</div>
                    </TableCell>
                    <TableCell>
                      <StatusBadge status={r.status} />
                      {r.error && <div style={{ fontSize: 11, color: STATUS_COLOR.failed, maxWidth: 220, overflow: "hidden", textOverflow: "ellipsis" }} title={r.error}>{r.error}</div>}
                    </TableCell>
                    <TableCell>{r.attempts}/{r.max_attempts}</TableCell>
                    <TableCell style={{ whiteSpace: "nowrap" }}>{fmt(r.scheduled_at)}</TableCell>
                    <TableCell style={{ whiteSpace: "nowrap" }}>{fmt(r.sent_at)}</TableCell>
                    <TableCell style={{ whiteSpace: "nowrap" }}>
                      <Button color="info" size="sm" onClick={() => openPreview(r.id)}>Ver</Button>
                      {r.status === "failed" && <Button color="success" size="sm" onClick={() => retry(r.id)}>Reintentar</Button>}
                      {(r.status === "pending" || r.status === "failed") && <Button color="danger" size="sm" onClick={() => cancelRow(r.id)}>Cancelar</Button>}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            {/* Paginación simple */}
            <div style={{ display: "flex", gap: 12, alignItems: "center", justifyContent: "flex-end", marginTop: 12 }}>
              <Button size="sm" disabled={pagination.current_page <= 1} onClick={() => fetchList(pagination.current_page - 1)}>◀</Button>
              <span>{pagination.current_page} / {pagination.last_page} ({pagination.total})</span>
              <Button size="sm" disabled={pagination.current_page >= pagination.last_page} onClick={() => fetchList(pagination.current_page + 1)}>▶</Button>
            </div>
          </CardBody>
        </Card>
      </GridItem>

      {/* Preview del contenido */}
      <Dialog open={!!preview} onClose={() => setPreview(null)} maxWidth="md" fullWidth>
        <DialogTitle>
          Envío #{preview && preview.id} — {preview && preview.channel}
          <div style={{ fontSize: 13, color: "#64748B", fontWeight: 400 }}>{preview && preview.recipient}</div>
        </DialogTitle>
        <DialogContent dividers>
          {preview && preview.subject && <p style={{ fontWeight: 600 }}>Asunto: {preview.subject}</p>}
          {preview && preview.channel === "email" ? (
            <div style={{ border: "1px solid #E2E8F0", borderRadius: 6, padding: 12, background: "#fff" }}
              dangerouslySetInnerHTML={{ __html: (preview && preview.body) || "" }} />
          ) : (
            <pre style={{ whiteSpace: "pre-wrap", fontFamily: "inherit" }}>{preview && preview.body}</pre>
          )}
          {preview && preview.error && <p style={{ color: STATUS_COLOR.failed, marginTop: 12 }}>Error: {preview.error}</p>}
        </DialogContent>
      </Dialog>
    </GridContainer>
  );
}
